import * as React from 'react';
import {
  progressTrackRecipe,
  usageMeterFillRecipe,
} from '../recipes/progress.recipe';
import { Inline, Stack } from './Layout';
import { Text } from './Text';

export interface UsageMeterProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** What is counted: "Drivers", "Calls this month". */
  label: string;
  used: number;
  /** The plan's limit. Null for unlimited: the count shows, the bar does not. */
  limit: number | null;
  /**
   * Close enough to the limit to say so. The app decides, from the plan
   * catalogue's threshold, so the two apps and the server agree on where
   * "near" starts.
   */
  near?: boolean;
}

/**
 * How much of a plan's limit is used: the count against the limit, and a bar.
 *
 *   <UsageMeter label="Drivers" used={3} limit={5} />
 *   <UsageMeter label="Calls this month" used={41} limit={50} near />
 *
 * The bar turns to warning when `near` and to destructive at the limit,
 * where the next one is refused. Unlimited shows the count alone; a bar
 * against infinity is always empty and says nothing.
 */
export const UsageMeter = React.forwardRef<HTMLDivElement, UsageMeterProps>(
  function UsageMeter({ label, used, limit, near = false, ...props }, ref) {
    const unlimited = limit === null;
    const full = !unlimited && used >= limit;
    const pct = unlimited || limit === 0 ? 100 : (used / limit) * 100;
    const words = unlimited ? `${used} · Unlimited` : `${used} of ${limit}`;
    return (
      <Stack ref={ref} gap={2} {...props}>
        <Inline gap={2} justify="between" align="baseline">
          <Text as="span" variant="body-sm-strong">
            {label}
          </Text>
          <Text
            as="span"
            variant="body-sm"
            tone={full ? 'destructive' : 'muted'}
            numeric
          >
            {words}
          </Text>
        </Inline>
        {unlimited ? null : (
          <div
            role="meter"
            aria-label={label}
            aria-valuenow={used}
            aria-valuemin={0}
            aria-valuemax={limit}
            aria-valuetext={`${label}: ${used} of ${limit}`}
            className={progressTrackRecipe()}
          >
            <div
              className={usageMeterFillRecipe({
                tone: full ? 'destructive' : near ? 'warning' : 'default',
              })}
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        )}
      </Stack>
    );
  }
);
